import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy } from "lucide-react";
import { buildBookingPageQrSvg, svgToDataUrl } from "../../features/trial-requests/utils/bookingPage";

/** How long Copy reads "Copied". */
export const COPIED_MS = 2000;

interface ShareLinkBlockProps {
  id: string;
  label: string;
  url: string;
  hint?: string;
  /** A link artwork (a store badge) shown instead of the written address. */
  badge?: ReactNode;
  /** False on the phone itself, where nobody can scan its own screen. */
  showQr?: boolean;
  qrAlt?: string;
  /** A smaller QR code and heading, for a block nested in another section. */
  compact?: boolean;
}

/** One address to hand on, stacked like the app's sheet: heading, hint, QR code, address, Copy. */
const ShareLinkBlock = ({ id, label, url, hint, badge, showQr = true, qrAlt, compact = false }: ShareLinkBlockProps) => {
  const { t } = useTranslation();
  const qr = useMemo(() => (showQr ? svgToDataUrl(buildBookingPageQrSvg(url)) : null), [url, showQr]);
  const Heading = compact ? "h4" : "h3";
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async (): Promise<void> => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_MS);
  };

  return (
    <div className={`space-y-3 text-center ${compact ? "py-3" : "py-5"}`} data-testid={`share-${id}`}>
      <Heading className="text-base font-semibold text-grey-20 dark:text-white">{label}</Heading>
      {hint && <p className="text-sm text-grey-40 dark:text-grey-80">{hint}</p>}
      {/* QR codes need a light background to scan — the white chip stays in dark mode too. */}
      {qr && (
        <img
          src={qr}
          alt={qrAlt ?? t("shareLinks.qrAlt")}
          data-testid={`share-${id}-qr`}
          className={`mx-auto rounded-lg border border-grey-90 bg-white p-1 dark:border-grey-50 ${compact ? "h-32 w-32" : "h-40 w-40"}`}
        />
      )}
      {badge ? (
        <div className="flex min-h-[58px] items-center justify-center">{badge}</div>
      ) : (
        <p
          data-testid={`share-${id}-url`}
          className="break-all rounded-lg bg-grey-95 px-3 py-2 text-sm text-grey-20 dark:bg-primary-20 dark:text-white"
        >
          {url}
        </p>
      )}
      <button type="button" className="btn btn-secondary" onClick={() => void copy()}>
        <Copy size={16} />
        {copied ? t("shareLinks.copied") : t("shareLinks.copy")}
      </button>
    </div>
  );
};

export default ShareLinkBlock;
